import { useEffect, useState } from 'react';

import {
  DividerHorizontalIcon,
  ExclamationTriangleIcon,
  TriangleDownIcon,
  TriangleUpIcon
} from '@radix-ui/react-icons';
import { EmptyState, Flex, Label, Radio } from '@raystack/apsara';
import * as _ from 'lodash';

import { Show } from '@src/hoc';
import { COLORMAP_TYPES } from '@src/utils/constants/APP';
import {
  CHANGE_DETECTION_VIEW_MODES,
  FOREST_BIOMASS_BLOCK_NAME
} from '@src/utils/constants/MODE_INFERENCE_DEFAULTS';
import { getUSTimeFormat } from '@src/utils/dateUtils';
import { getCogURLForOutput } from '@src/utils/workFlowUtils';

import { CategoricalView } from './CDCommonComponents/CategoricalMode/CategoricalView';
import ImageLayerList from './CDCommonComponents/CompareMode/ImageLayersList';
import { DeltaGraph } from './CDCommonComponents/DeltaMode/DeltaGraph';
import InputTile from './CDCommonComponents/InputTile';

export const getDateForAsset = (blockName, date) => {
  if (blockName === FOREST_BIOMASS_BLOCK_NAME) return date;
  return getUSTimeFormat(date);
};

const getIcon = diff => {
  if (diff === 0)
    return (
      <DividerHorizontalIcon color='var(--rs-color-background-neutral-emphasis)' />
    );
  else if (diff > 0)
    return (
      <TriangleUpIcon color='var(--rs-color-foreground-success-primary)' />
    );
  else
    return (
      <TriangleDownIcon color='var(--rs-color-foreground-danger-primary)' />
    );
};

export const ChangeDetectionCommon = ({
  graphData,
  viewMode,
  handleViewChange,
  asset_ref,
  asset_comp,
  loader,
  layers,
  onLayersChange,
  setChildClasses,
  layersLoading
}) => {
  const [activeGraph, setActiveGraph] = useState([]);
  const [stackGraph, setStackGraph] = useState([]);
  const [categoricalData, setCategoricalData] = useState({});

  const parseCategoricalData = graphData => {
    try {
      const data = graphData['categorical_stats'];

      const result = {};

      // Get the length of data entries, assuming all columns have the same length
      const numRows = data['Reference class name (Before)'].length;

      for (let i = 0; i < numRows; i++) {
        const oldClassName = data['Reference class name (Before)'][i];
        const newClassName = data['Comparison class name (After)'][i];

        // Create an entry object with other fields
        const entry = {
          'Comparison class name (After)': newClassName,
          'Class index': data['Categorical class index'][i],
          // Count: data['Count'][i],
          colors: data['Categorical class colour'][i],
          'Area in km2': data['Change in area (in sq. Kms.)'][i],
          'Area in percentage': data['Change in area (in %)'][i]
        };

        // Initialize array for each unique `Reference class name (Before)` if not already created
        if (!result[oldClassName]) {
          result[oldClassName] = [];
        }

        // Append entry to the list of the current `Reference class name (Before)`
        result[oldClassName].push(entry);
      }

      setCategoricalData(result);
    } catch (error) {
      console.error('Error: No categorical data found', error);
    }
  };

  // fetch categorical data based on viewMode

  useEffect(() => {
    if (graphData && !_.isEmpty(graphData)) {
      // categorical to only support for v3 changes
      if (viewMode === CHANGE_DETECTION_VIEW_MODES.categorical) {
        parseCategoricalData(graphData);
      }

      const outputObj = graphData['raster_stats'] || graphData['path3_diff'];

      let classNames = {
        ...outputObj['Class Name']
      };

      let ranges = { ...outputObj['Class Range'] };

      let changeDict = {
        ...outputObj['Change in Area Percentage']
      };

      let changeDictArea = {
        ...outputObj['Change in Area (Sq KM)']
      };

      const classColors = { ...outputObj['Class Color'] };

      const compClassPercentage = {
        ...outputObj['Area Percentage (Comparison asset)']
      };
      const compClassAreaKm = {
        ...outputObj['Area  in Sq KM (Comparison asset)']
      };
      const refClassPercentage = {
        ...outputObj['Area Percentage (Reference asset)']
      };
      const refClassAreaKm = {
        ...outputObj['Area  in Sq KM (Reference asset)']
      };

      const data = Object.keys(changeDict).map(ind => {
        let obj = {
          class: classNames[ind],
          difference:
            changeDict[ind] > 0
              ? [0, Number(changeDict[ind])]
              : [Number(changeDict[ind]), 0],
          differenceInKm: changeDictArea[ind],
          range: ranges[ind]?.replace(' - ', ' to '),
          color: classColors[ind],
          refPercentage: refClassPercentage[ind],
          refArea: refClassAreaKm[ind],
          compPercentage: compClassPercentage[ind],
          compArea: compClassAreaKm[ind],
          total: changeDict[ind],
          differenceIcon: getIcon(changeDict[ind])
        };
        return obj;
      });
      setActiveGraph(data);

      let refData = { name: 'Reference', graphData: data };
      Object.keys(classNames).forEach(ind => {
        refData[classNames[ind]] = refClassPercentage[ind];
      });
      let compData = { name: 'Comparison', graphData: data };
      Object.keys(classNames).forEach(ind => {
        compData[classNames[ind]] = compClassPercentage[ind];
      });
      setStackGraph([refData, compData]);
    }
  }, [graphData, viewMode]);

  const inputTiles = (
    <>
      {/* input 1 */}
      <InputTile
        title='Reference Asset'
        name={asset_ref.name}
        description={asset_ref.description}
        src={asset_ref.src}
      />

      {/* Input 2 */}
      <InputTile
        title='Comparison Asset'
        name={asset_comp.name}
        description={asset_comp.description}
        src={asset_comp.src}
      />
    </>
  );

  const radioGroups = [
    {
      id: 'raster_of_delta',
      label: 'Raster of delta',
      value: CHANGE_DETECTION_VIEW_MODES.deltaRaster,
      comp: (
        <>
          {inputTiles}

          <DeltaGraph
            isLoading={loader}
            barGraphData={activeGraph}
            stackGraphData={stackGraph}
          />
        </>
      )
    },
    {
      id: 'categorical_view',
      label: 'Categorical',
      value: CHANGE_DETECTION_VIEW_MODES.categorical,
      comp: (
        <>
          {inputTiles}

          <Show
            when={!_.isEmpty(categoricalData)}
            fallback={
              <EmptyState
                icon={<ExclamationTriangleIcon />}
                heading='No Categorical data!'
                subHeading='This seems to be an insight run on an older version of the model. Try running the model again!'
              />
            }
          >
            <CategoricalView
              isLoading={loader}
              data={categoricalData}
              setChildClasses={setChildClasses}
            />
          </Show>
        </>
      )
    },
    {
      id: 'compare',
      label: 'Compare',
      value: CHANGE_DETECTION_VIEW_MODES.compare,
      comp: (
        <ImageLayerList
          layers={layers}
          onLayersChange={onLayersChange}
          layersLoading={layersLoading}
        />
      )
    }
  ];

  return (
    <>
      <Radio
        value={viewMode}
        onValueChange={handleViewChange}
        style={{ width: '100%' }}
      >
        <Flex
          justify='between'
          align='center'
          gap='small'
          style={{ width: '100%' }}
        >
          {radioGroups.map(d => (
            <Flex key={d.id} gap='small' align='center'>
              <Radio.Item
                id={d.id}
                value={d.value}
                data-test-id='show-raster-delta-radio'
              />

              <Label htmlFor={d.id} size='medium'>
                {d.label}
              </Label>
            </Flex>
          ))}
        </Flex>
      </Radio>

      {radioGroups.find(d => d.value === viewMode)?.comp}
    </>
  );
};

// handle url generation for categorical raster based on child classes
export const getURLForChildClassesCategorical = (URL, classes, format) => {
  // creating a spoof V1Alpha1Output object to force getTileLayerForOutput to
  const categoricalOutput = {
    value: URL,
    properties: {
      visualisation: {
        type: COLORMAP_TYPES.discreteColormap,
        discrete: { ...classes }
      }
    }
  };

  return getCogURLForOutput(categoricalOutput, format);
};
